import { useCallback, useEffect, useState } from 'react';

import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { IconButton, Paper, Stack, Typography } from '@mui/material';

import { themeColors } from 'colors';
import useGenerateCopy from 'hooks/mutations/useGenerateCopy';
import useCopyGeneratorHistory from 'hooks/queries/useCopyGeneratorHistory';

import BrandNotRegisteredCTA from './BrandNotRegisteredCTA';
import ChatComposer from './ChatComposer';
import MessagesDisplay from './MessagesDisplay';
import SaraCircleMark from './SaraCircleMark';
import { ChatMessage, CopyGeneratorChannel, COPY_GENERATOR_ERROR_CODE_BRAND_NOT_REGISTERED } from './utils';

import type { CuredApiError } from 'models/types';

const CHAT_HEADER_BG = themeColors.primary.main;

export type Props = {
    open: boolean;
    onClose: () => void;
    /**
     * Channel context for copy generation. Determines which AI persona/instructions are used.
     * Defaults to 'email' for backward compatibility with the email builder.
     */
    channel?: CopyGeneratorChannel;
    /**
     * Which edge of the viewport the panel anchors to.
     * - 'left' (default) — used by the email builder; floats above the Sara FAB on the left.
     * - 'right' — used by the SMS builder; pinned to the right side, away from the Sara FAB.
     */
    placement?: 'left' | 'right';
    /**
     * ID of the content template currently open in the editor.
     * Passed through to the backend so it can resume the last conversation for this template.
     * Pass null (or omit) when the user is creating new content — history will not be loaded.
     */
    templateId?: number | null;
};

/**
 * Shell for the Ask Sara copy-generator chat (email, SMS, and other content builders).
 * Call sites should gate rendering with LaunchDarkly `ai-brand-tools` (or equivalent).
 *
 * Session lifecycle: a fresh session is created on the first message of each panel open.
 * Closing the panel resets sessionId so reopening always starts a new session.
 */
const CopyGeneratorChat = ({ open, onClose, channel = 'email', placement = 'left', templateId }: Props) => {
    const [composerText, setComposerText] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showBrandNotRegistered, setShowBrandNotRegistered] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    const { data: history } = useCopyGeneratorHistory(templateId ?? 0, {
        enabled: !!templateId && open,
    });

    useEffect(() => {
        if (!open) {
            setHistoryLoaded(false);
            setIsMinimized(false);
            return;
        }
        if (historyLoaded || !templateId) return;
        if (history === undefined) return; // still loading

        if (history) {
            setSessionId(history.sessionId);
            setMessages(
                history.messages.map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    feedback: m.feedback,
                }))
            );
        }
        setHistoryLoaded(true);
    }, [open, history, historyLoaded, templateId]);

    const { mutate: generateCopy, isLoading } = useGenerateCopy({
        onSuccess: (response) => {
            setSessionId(response.sessionId);
            setMessages((prev) => [
                ...prev,
                { id: response.message.id, role: response.message.role, content: response.message.content },
            ]);
            setErrorMessage(null);
            setShowBrandNotRegistered(false);
        },
        onError: (error) => {
            const code = (error as CuredApiError).errorCode;
            if (code === COPY_GENERATOR_ERROR_CODE_BRAND_NOT_REGISTERED) {
                setShowBrandNotRegistered(true);
                setErrorMessage(null);
                return;
            }
            setErrorMessage(error.message);
        },
    });

    const handleClose = useCallback(() => {
        setSessionId(null);
        setMessages([]);
        setHistoryLoaded(false);
        setIsMinimized(false);
        onClose();
    }, [onClose]);

    const handleToggleMinimize = useCallback(() => setIsMinimized((prev) => !prev), []);

    const handleSend = useCallback(() => {
        const trimmed = composerText.trim();
        if (!trimmed) return;

        // Optimistically add the user message with a temporary ID.
        // The backend does not return user message IDs — only assistant message IDs.
        // Negative temp ID avoids colliding with SERIAL (always positive) values from the backend.
        const userMessage: ChatMessage = { id: -Date.now(), role: 'user', content: trimmed };
        setMessages((prev) => [...prev, userMessage]);
        setComposerText('');
        setErrorMessage(null);
        setShowBrandNotRegistered(false);

        generateCopy({ sessionId: sessionId ?? undefined, channel, message: trimmed, templateId });
    }, [composerText, sessionId, generateCopy, channel, templateId]);

    const handleRetry = useCallback(() => {
        if (isLoading || messages.length === 0 || !sessionId) return;

        let lastUserMessage: ChatMessage | null = null;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'user') {
                lastUserMessage = messages[i];
                break;
            }
        }
        if (!lastUserMessage) return;

        // Remove the last assistant reply so the loading state renders cleanly.
        setMessages((prev) => {
            const lastAssistantIndex = [...prev].reverse().findIndex((m) => m.role === 'assistant');
            if (lastAssistantIndex === -1) return prev;
            return prev.slice(0, prev.length - 1 - lastAssistantIndex);
        });
        setErrorMessage(null);
        setShowBrandNotRegistered(false);

        generateCopy({ sessionId, channel, message: lastUserMessage.content, templateId });
    }, [messages, isLoading, sessionId, generateCopy, channel, templateId]);

    const isSendDisabled = composerText.trim().length === 0 || isLoading;

    if (!open) {
        return null;
    }

    return (
        <Paper
            data-testid="copy-generator-chat-panel"
            data-channel={channel}
            data-placement={placement}
            elevation={8}
            role="dialog"
            aria-modal="false"
            aria-label="Ask Sara"
            sx={(theme) => ({
                position: 'fixed',
                ...(placement === 'right' ? { right: theme.spacing(16) } : { left: theme.spacing(16) }),
                bottom: theme.spacing(0),
                width: 420,
                maxHeight: 'min(608px, calc(100vh - 120px))',
                zIndex: 1290,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '6px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
            })}
        >
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                sx={{
                    bgcolor: CHAT_HEADER_BG,
                    minHeight: 48,
                    flexShrink: 0,
                    px: 1,
                    pl: 2,
                }}
            >
                <Stack alignItems="center" direction="row" gap={1.5}>
                    <SaraCircleMark size={32} />
                    <Typography
                        component="h2"
                        sx={{
                            color: 'common.white',
                            fontWeight: 500,
                            fontSize: 16,
                            lineHeight: 1.5,
                            letterSpacing: '2px',
                            textTransform: 'capitalize',
                        }}
                    >
                        Ask Sara
                    </Typography>
                </Stack>
                <Stack alignItems="center" direction="row">
                    <IconButton
                        aria-label={isMinimized ? 'Expand Ask Sara' : 'Minimize Ask Sara'}
                        aria-expanded={!isMinimized}
                        data-testid="copy-generator-chat-minimize"
                        size="small"
                        sx={{ color: 'common.white' }}
                        onClick={handleToggleMinimize}
                    >
                        {isMinimized ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <IconButton
                        aria-label="Close Ask Sara"
                        data-testid="copy-generator-chat-close"
                        size="small"
                        sx={{ color: 'common.white' }}
                        onClick={handleClose}
                    >
                        <Close />
                    </IconButton>
                </Stack>
            </Stack>

            {!isMinimized &&
                (showBrandNotRegistered ? (
                    <BrandNotRegisteredCTA onDismiss={handleClose} />
                ) : (
                    <>
                        <MessagesDisplay
                            messages={messages}
                            errorMessage={errorMessage}
                            isLoading={isLoading}
                            onRetry={handleRetry}
                        />

                        <ChatComposer
                            value={composerText}
                            onChange={setComposerText}
                            onSend={handleSend}
                            disabled={isSendDisabled}
                        />
                    </>
                ))}
        </Paper>
    );
};

export default CopyGeneratorChat;
